import React, { useCallback, useContext, useEffect, useState } from 'react'
import { RouteComponentProps } from 'react-router'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { ThemeContext } from 'styled-components'

import { api } from 'services/api'
import { useToast } from 'contexts/ToastContext'
import { useModule } from 'contexts/ModuleContext'

import * as S from './styles'

const schema = yup.object().shape({
  xPicPayToken: yup.string().required('A x-picpay-token é obrigatória'),
  xSellerToken: yup.string().required('O x-seller-token é obrigatória')
})

interface FormDataProps {
  xPicPayToken?: string
  xSellerToken?: string
}

interface Props extends RouteComponentProps {}

const PicPayModule: React.FC<Props> = ({ history }) => {
  const { showToast } = useToast()
  const {
    register,
    setValue,
    watch,
    reset,
    handleSubmit,
    formState: { errors }
  } = useForm<FormDataProps>({
    mode: 'onBlur',
    resolver: yupResolver(schema)
  })
  const themeContext = useContext(ThemeContext)

  const { module, setModule } = useModule()

  const [isLoading, setIsLoading] = useState(false)
  const previewXPicPayToken = watch('xPicPayToken')
  const previewXSellerToken = watch('xSellerToken')

  useEffect(() => {
    setValue('xPicPayToken', module.picpayModule?.xPicPayToken, {
      shouldValidate: true
    })
    setValue('xSellerToken', module.picpayModule?.xSellerToken, {
      shouldValidate: true
    })
  }, [module, setValue])

  const handleCancel = () => history.push('/dashboard/modules/')

  const handleButtonHelper = () =>
    window.open('https://lojista.picpay.com/ecommerce-token', '_blank')

  const handleUpdateStatus = useCallback(async () => {
    try {
      const picpayModuleData = {
        ...module.picpayModule,
        active: !module.picpayModule.active
      }

      const { data: response } = await api('user-api').put(
        `modules/picpay/${module.picpayModule.id}`,
        picpayModuleData
      )

      setModule({
        ...module,
        picpayModule: picpayModuleData
      })

      showToast({
        type: 'success',
        message: 'Módulo atualizado com sucesso!'
      })
    } catch (err) {
      showToast({
        type: 'error',
        message: 'Aconteceu um erro!'
      })
    }
  }, [setModule, module, showToast])

  const onSubmit = useCallback(
    async (data: FormDataProps) => {
      setIsLoading(true)

      try {
        const picpayModuleData = {
          xPicPayToken:
            data.xPicPayToken.trim().length > 0 ? data.xPicPayToken : null,
          xSellerToken:
            data.xSellerToken.trim().length > 0 ? data.xSellerToken : null
        }

        const { data: response } = await api('user-api').put(
          `modules/picpay/${module.picpayModule.id}`,
          picpayModuleData
        )

        setModule({
          ...module,
          picpayModule: response
        })

        showToast({
          type: 'success',
          message: 'Módulo atualizado com sucesso!'
        })
      } catch (err) {
        showToast({
          type: 'error',
          message: 'Aconteceu um erro!'
        })
      } finally {
        setIsLoading(false)
      }
    },
    [module, setModule, showToast]
  )

  return (
    <S.Container>
      <S.Title>
        <h1>PicPay</h1>

        <S.Helper>
          Para pegar as credencias, clique{' '}
          <button type="button" onClick={handleButtonHelper}>
            aqui
          </button>
        </S.Helper>

        <S.ModuleStatus
          checkedIcon={false}
          uncheckedIcon={false}
          height={20}
          width={60}
          handleDiameter={12}
          offColor={themeContext.colors.red}
          onColor={themeContext.colors.green}
          checked={module.picpayModule.active}
          disabled={
            isLoading ||
            !previewXPicPayToken ||
            previewXPicPayToken?.trim().length === 0 ||
            !previewXSellerToken ||
            previewXSellerToken?.trim().length === 0
          }
          onChange={handleUpdateStatus}
        />
      </S.Title>

      <S.Form onSubmit={handleSubmit(onSubmit)}>
        <S.Col>
          <S.Label htmlFor="xPicPayToken">
            Digite aqui o x-picpay-token da picpay.
          </S.Label>

          <S.Input
            id="xPicPayToken"
            name="xPicPayToken"
            className={errors.xPicPayToken && 'is-invalid'}
            disabled={isLoading}
            {...register('xPicPayToken')}
          />
        </S.Col>

        <S.Col>
          <S.Label htmlFor="xSellerToken">
            Digite aqui o x-seller-token da picpay.
          </S.Label>

          <S.Input
            id="xSellerToken"
            name="xSellerToken"
            type="password"
            className={errors.xSellerToken && 'is-invalid'}
            disabled={isLoading}
            {...register('xSellerToken')}
          />
        </S.Col>

        <S.Buttons>
          <S.Button type="button" cancel onClick={handleCancel}>
            Cancelar
          </S.Button>

          <S.Button
            type="submit"
            disabled={
              isLoading ||
              !previewXPicPayToken ||
              previewXPicPayToken?.trim().length === 0 ||
              !previewXSellerToken ||
              previewXSellerToken?.trim().length === 0
            }
          >
            Salvar
          </S.Button>
        </S.Buttons>
      </S.Form>
    </S.Container>
  )
}

export default PicPayModule
